/**
 * Tournament Ranking Tab (Campaign Variant)
 * Shows tournament leaderboard and player rankings, reading data from the campaigns store.
 */

import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import ActivePlayers from './ActivePlayers/ActivePlayers';
import TablePlayerRow from './TablePlayerRow/TablePlayerRow';
import { useSelectedCampaign, TOURNAMENTS_CAMPAIGN_TYPE } from 'features/campaigns';
import { useTournamentLeaderboard } from '../../../../../hooks/data/useTournamentLeaderboard';
import { findTranslationKeyAsGivenJSONTranslationFallback } from 'shared/utils/helpers';

import styles from './TournamentRankingTab.module.css';
import tableStyles from '../../../../shared/CampaignTable/CampaignTable.module.css';

interface LeaderboardEntry {
  position: number;
  playerId: string;
  playerName: string;
  score: number;
  isCurrentPlayer?: boolean;
}

interface RankingTabProps {
  campaignId?: string;
  isActive?: boolean;
}

const RankingTab: React.FC<RankingTabProps> = ({ isActive = true }) => {
  const tournament = useSelectedCampaign(TOURNAMENTS_CAMPAIGN_TYPE);

  const {
    data: leaderboardData,
    isLoading: isLeaderboardLoading,
    isError: isLeaderboardError,
  } = useTournamentLeaderboard(tournament?.id || '', {
    enabled: isActive && !!tournament,
    refetchInterval: 5000,
  });

  const leaderboard = leaderboardData?.leaderboard || [];
  const totalActivePlayers = leaderboardData?.totalActivePlayers || 0;

  const tournamentTiming = useMemo(() => {
    if (!tournament) return null;
    const now = new Date();
    const startDate = new Date(tournament.startDate);
    const endDate = new Date(tournament.endDate);
    return {
      isUpcoming: now < startDate,
      isActive: now >= startDate && now <= endDate,
      isFinished: now > endDate,
    };
  }, [tournament]);

  const shouldShowActivePlayers = totalActivePlayers > 0 && !tournamentTiming?.isFinished;

  const { t } = useTranslation();

  if (!tournament || isLeaderboardLoading) {
    return (
      <div className={tableStyles.tabContainer}>
        <div className={styles.loaderContainer}>
          <LoadingIndicator show absolute />
        </div>
      </div>
    );
  }

  if (isLeaderboardError) {
    return (
      <div className={tableStyles.tabContainer}>
        <div className={styles.noDataWrapper} data-players={shouldShowActivePlayers}>
          <p className={styles.noDataText}>{t('tournaments.ranking_error', 'Failed to load rankings')}</p>
        </div>
      </div>
    );
  }

  if (tournamentTiming?.isUpcoming) {
    return (
      <div className={tableStyles.tabContainer}>
        <div className={styles.noDataWrapper} data-players={shouldShowActivePlayers}>
          <div className={styles.noDataText}>
            {t('tournaments.starts_soon', 'Tournament Starting Soon')}
            <br />
            <p className={styles.highlightedText}>
              {t('tournaments.get_ready', 'Rankings will be available once the tournament begins.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!leaderboard || leaderboard.length === 0) {
    return (
      <div className={tableStyles.tabContainer}>
        <div className={styles.noDataWrapper} data-players={shouldShowActivePlayers}>
          <p className={styles.noDataText}>
            {t('no_active_players', findTranslationKeyAsGivenJSONTranslationFallback('no_active_players'))}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.tableWrapper}>
        <div
          className={styles.tableContainer}
          data-finished={tournamentTiming?.isFinished}
          data-players={shouldShowActivePlayers}
        >
          <table className={styles.tableWrapperInner}>
            <tbody className={styles.tableBody}>
              <tr className={clsx(styles.tableRow, styles.tableHeaderRow)}>
                <th className={styles.tableHeaderCell}>
                  <span className={styles.playerSimbol}>#</span>
                  <span className={styles.playerCount}>
                    {t('generic_player', findTranslationKeyAsGivenJSONTranslationFallback('generic_player'))}
                  </span>
                </th>
                <th className={clsx(styles.tableHeaderCell, styles.genericPointsCell)}>
                  {t('generic_points', findTranslationKeyAsGivenJSONTranslationFallback('generic_points'))}
                </th>
              </tr>
              {shouldShowActivePlayers && <ActivePlayers totalActivePlayers={totalActivePlayers} />}
              {leaderboard.map((score: LeaderboardEntry) => (
                <TablePlayerRow score={score} key={score.position} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default memo(RankingTab);
